/**
 * leadPipeline — display-only mapping of the 13 backend statuses to the
 * 9-step commercial pipeline + "Lead frío" exit that the team actually uses.
 *
 * Backend keys (LeadIn.status Literal) are untouched. This file simply
 * decides which step a lead currently belongs to for rendering, and what
 * label to show. Old leads in dossier_pending / dossier / reservation_sent
 * remain queryable and editable — they just surface in the canonical
 * adjacent step until the user advances them through the new UI.
 */

export const PRIMARY_PIPELINE = [
  { key: "new",                step: 1, label: "Nuevo lead",         shortLabel: "Nuevo" },
  { key: "contacted",          step: 2, label: "Contactado",          shortLabel: "Contact." },
  { key: "meeting_scheduled",  step: 3, label: "Reunión agendada",    shortLabel: "Reunión" },
  { key: "call_done",          step: 4, label: "Reunión realizada",   shortLabel: "Realizada" },
  { key: "in_followup",        step: 5, label: "En negociación",      shortLabel: "Negoc." },
  { key: "villa_on_hold",      step: 6, label: "Villa en hold",       shortLabel: "Hold" },
  { key: "reserved",           step: 7, label: "Reserva pagada",      shortLabel: "Reserva" },
  { key: "contract_signed",    step: 8, label: "Contrato firmado",    shortLabel: "Contrato" },
  { key: "closed_won",         step: 9, label: "Cerrado ganado",      shortLabel: "Ganado" },
];

export const PIPELINE_KEY_TO_STEP = Object.fromEntries(
  PRIMARY_PIPELINE.map((s) => [s.key, s.step])
);

// Backend key that represents the "cold lead" / lost branch. Kept as `lost`
// internally to avoid a schema migration; UI shows "Lead frío".
export const COLD_LEAD_STATUS = "lost";
export const COLD_LEAD_LABEL  = "Lead frío";
export const COLD_LEAD_DOT_COLOR = "#7dd3fc"; // sky-300 — visually distinct from active stages

// Maps any backend status (including legacy PR#9 intermediates) to the
// pipeline key that should be highlighted in the UI. Used by the stepper
// and the LeadCard chip to keep visual noise low.
export const STATUS_TO_VISIBLE_STEP = {
  new:                "new",
  contacted:          "contacted",
  meeting_scheduled:  "meeting_scheduled",
  dossier_pending:    "meeting_scheduled",
  dossier:            "call_done",
  call_done:          "call_done",
  in_followup:        "in_followup",
  villa_on_hold:      "villa_on_hold",
  reservation_sent:   "villa_on_hold",
  reserved:           "reserved",
  contract_signed:    "contract_signed",
  closed_won:         "closed_won",
  lost:               COLD_LEAD_STATUS,
  // Legacy aliases also handled by normalizeStatus, kept here for safety.
  meeting:            "meeting_scheduled",
  signed:             "reserved",
};

export function visibleStep(status) {
  return STATUS_TO_VISIBLE_STEP[status] || "new";
}

/** True when the lead is on the cold/lost branch. */
export function isColdLead(status) {
  return visibleStep(status) === COLD_LEAD_STATUS;
}

/** Pipeline label for the given backend status (shows "Lead frío" for lost). */
export function pipelineLabel(status) {
  if (isColdLead(status)) return COLD_LEAD_LABEL;
  const k = visibleStep(status);
  return PRIMARY_PIPELINE.find((s) => s.key === k)?.label || k;
}

/** Step number (1-9) for primary pipeline, or null for cold leads. */
export function pipelineStep(status) {
  if (isColdLead(status)) return null;
  return PIPELINE_KEY_TO_STEP[visibleStep(status)] || 1;
}

// Per-stage dot color borrowed from leadStatuses.js (single source for hues
// in active stages). Cold gets its own sky-300 to read as out-of-pipeline.
const STAGE_DOT_COLOR = {
  new:                "#94a3b8",
  contacted:          "#3b82f6",
  meeting_scheduled:  "#06b6d4",
  call_done:          "#0ea5e9",
  in_followup:        "#f59e0b",
  villa_on_hold:      "#fb923c",
  reserved:           "#10b981",
  contract_signed:    "#059669",
  closed_won:         "#047857",
};

// 10 visible groups (9 primary pipeline + Lead frío) used by the Stages
// accordion, the Marketing kanban and the LeadCard menu. Order matters.
export const VISIBLE_PIPELINE_WITH_COLD = [
  ...PRIMARY_PIPELINE.map((s) => ({
    key: s.key,
    step: s.step,
    label: s.label,
    shortLabel: s.shortLabel,
    dotColor: STAGE_DOT_COLOR[s.key],
  })),
  {
    key: COLD_LEAD_STATUS,
    step: null,
    label: COLD_LEAD_LABEL,
    shortLabel: "Frío",
    dotColor: COLD_LEAD_DOT_COLOR,
  },
];

/** Returns the next status the user can move to from currentStatus.
 *  - Cold lead → reactivates to "new".
 *  - Last primary step (closed_won) → stays.
 *  - Otherwise → next primary step.
 */
export function nextVisibleStatus(currentStatus) {
  if (isColdLead(currentStatus)) return PRIMARY_PIPELINE[0].key;
  const step = pipelineStep(currentStatus);
  if (step == null) return PRIMARY_PIPELINE[0].key;
  const idx = PRIMARY_PIPELINE.findIndex((s) => s.step === step);
  if (idx === -1 || idx === PRIMARY_PIPELINE.length - 1) return null;
  return PRIMARY_PIPELINE[idx + 1].key;
}

/** Returns the previous primary status, or null if the lead is at step 1.
 *  Cold leads do not have a "prev" — use nextVisibleStatus to reactivate. */
export function prevVisibleStatus(currentStatus) {
  if (isColdLead(currentStatus)) return null;
  const step = pipelineStep(currentStatus);
  if (step == null || step <= 1) return null;
  const idx = PRIMARY_PIPELINE.findIndex((s) => s.step === step);
  if (idx <= 0) return null;
  return PRIMARY_PIPELINE[idx - 1].key;
}

/** True when moving from currentStatus to nextStatus deserves a confirm.
 *  Rules: closed_won, lost, any backward move, any forward jump ≥ 2 steps,
 *  leaving cold to reactivate. Adjacent forward = no confirm. */
export function needsConfirmMove(currentStatus, nextStatus) {
  if (!nextStatus || nextStatus === currentStatus) return false;
  if (nextStatus === COLD_LEAD_STATUS) return true;
  if (nextStatus === "closed_won") return true;
  if (isColdLead(currentStatus)) return true;
  const currStep = pipelineStep(currentStatus);
  const nextStep = pipelineStep(nextStatus);
  if (currStep == null || nextStep == null) return false;
  if (nextStep < currStep) return true;
  if (nextStep - currStep > 1) return true;
  return false;
}

/** Confirmation copy for a move that needsConfirmMove returned true for.
 *  Terminal/exit destinations keep their action-oriented copy ("Marcar como…");
 *  all other moves (backward, forward jumps ≥2, reactivating a cold lead)
 *  share a single "Cambiar estado a 'X'" phrasing so the user always sees the
 *  destination spelled out in the same way. */
export function confirmMoveText(nextStatus, currentStatus) {
  if (nextStatus === COLD_LEAD_STATUS) {
    return "Marcar como Lead frío. ¿Continuar?";
  }
  if (nextStatus === "closed_won") {
    return "Marcar como Cerrado ganado. Esta acción es importante. ¿Continuar?";
  }
  return `Cambiar estado a "${pipelineLabel(nextStatus)}". ¿Continuar?`;
}
